import { z } from "zod";
import { trimmed, uuid } from "./schemas/common";

/**
 * Guest messaging: the couple emails guests through Bridewell — an invitation (date, venue, their message, the
 * household's RSVP link) or an update. Mail to third parties on the couple's behalf, so every number here is an
 * abuse control as much as a product rule. Metered on the wedding owner's `emailsPerMonth` (the same allowance as
 * reminder mail); Free (0) cannot send.
 */
export const GUEST_MAIL = {
  /** Sends (messages, not addresses) per wedding in any 24 hours. */
  maxSendsPerDay: 3,
  maxSubjectChars: 120,
  maxMessageChars: 2000,
  /** Addresses one message may go to (the guest list cap of the biggest wedding fits). */
  maxRecipients: 500,
  /** Sent during the request (one provider batch call); the rest waits for the hourly cron. */
  sendNowBatch: 50,
  /** A recipient whose delivery failed this many times is marked failed. */
  maxAttempts: 3,
} as const;

export const GUEST_MAIL_KIND = ["invitation", "update"] as const;
export type GuestMailKind = (typeof GUEST_MAIL_KIND)[number];
export const GUEST_MAIL_AUDIENCE = ["all", "pending", "selected"] as const;
export type GuestMailAudience = (typeof GUEST_MAIL_AUDIENCE)[number];

export const guestMailSchema = z.object({
  kind: z.enum(GUEST_MAIL_KIND),
  /** Empty on an invitation = "{couple} — you're invited"; an update needs its own. */
  subject: trimmed(GUEST_MAIL.maxSubjectChars).default(""),
  message: z.string().trim().min(1, "Write a message.").max(GUEST_MAIL.maxMessageChars),
  includeRsvpLink: z.boolean().default(true),
  audience: z.enum(GUEST_MAIL_AUDIENCE),
  guestIds: z.array(uuid).max(GUEST_MAIL.maxRecipients).optional(),
}).superRefine((v, ctx) => {
  if (v.kind === "update" && !v.subject) ctx.addIssue({ code: "custom", path: ["subject"], message: "Give the update a subject." });
  if (v.audience === "selected" && !v.guestIds?.length) ctx.addIssue({ code: "custom", path: ["guestIds"], message: "Choose at least one guest." });
});
export type GuestMailInput = z.infer<typeof guestMailSchema>;

export interface GuestMailRecipientsSummary {
  /** Guests the audience picked. */
  guests: number;
  /** Distinct addresses the mail would go to. */
  addresses: number;
  households: number;
  noEmail: number;
  optedOut: number;
  /** Second guest rows with an address already on the list. */
  duplicates: number;
}

export interface GuestMailPreviewDTO {
  subject: string;
  /** The plain-text part exactly as the first recipient would get it. */
  text: string;
  recipients: GuestMailRecipientsSummary;
  /** Whether the mail carries the household's RSVP link (RSVP links are on and open). */
  rsvpLink: boolean;
  quota: { used: number; limit: number | null };
  sendsToday: number;
}

export interface GuestMessageDTO {
  id: string;
  kind: GuestMailKind;
  subject: string;
  message: string;
  includeRsvpLink: boolean;
  senderName: string;
  createdAt: string;
  recipientCount: number;
  skippedCount: number;
  sentCount: number;
  failedCount: number;
  /** Still waiting for the provider budget; the hourly job keeps sending. */
  queuedCount: number;
}

/** "{a} & {b} via Bridewell": the From display name (control characters, quotes and angle brackets removed). */
export function guestMailFromName(coupleTitle: string): string {
  const clean = coupleTitle.replace(/[\u0000-\u001f\u007f"<>\\]/g, " ").replace(/\s+/g, " ").trim().slice(0, 60);
  return clean ? `${clean} via Bridewell` : "Bridewell";
}
